import * as React from "react"
import { observable, computed, action, autorun } from "mobx"
import { GeneralStep } from "./steps/General/GeneralStep"
import { MembersStep } from "./steps/Members/MembersStep"
import { WaiversStep } from "./steps/Waivers/WaiversStep"
import { DayPickerStep } from "./steps/DayPicker/DayPickerStep"
import { Modal } from "./utils/ModalComponents/Modal"
import { MemberForm } from "./steps/Members/MemberForm"
import { PassesStep } from "./steps/Passes/PassesStep"
import { PayStep } from "./steps/Pay/PayStep"
import { BookingDB, LookupRequest } from "./db/BookingLightDB"
import { uuid } from "../light/utils"
import { Member, $Member } from "./db/Member"
import { SessionType, $SessionType } from "./db/SessionType"
import { Booking } from "./db/Booking"
import dayjs = require("dayjs")
import { DailyQuickView } from "./DailyQuickView/DailyQuickView"
import { createBrowserHistory, History } from "history"
import { DailyQuickViewComp } from "./DailyQuickView/DailyQuickViewComp"
import { StepsRoot } from "./StepsRoot"

export interface MembersMapI {
  db: BookingDB
  memberList: Member[]
  onEdit: (member: Member) => void
  onDelete: (member: Member) => void
  isEditable: boolean
  isSelectable: boolean
  has: (member: Member) => boolean
  toggleMembership: (member: Member) => void
}

export interface Step {
  flow: BookingFlow
  db: BookingDB
  name: string
  instruction?: string
  help?: string
}

type Comp = React.ComponentClass<any> | React.SFC<any>

interface BookingFlowComponents {
  general: Comp
  members: Comp
  waivers: Comp
  daypicker: Comp
  passes: Comp
  pay: Comp
  notReady: Comp
  modal: Comp
  memberForm: Comp | React.SFC<any>
  daypickerForm: Comp | React.SFC<any>
}

export class BookingFlow {
  constructor(componentMap: BookingFlowComponents) {
    this.componentMap = componentMap

    this.initSteps()
    this.initBrowserHistory()
    this.initSessionTypes()
    this.initWatchingEditingMember()
  }

  initSteps() {
    this.currentBooking = new Booking(this.db)

    this.generalStep = new GeneralStep()
    this.generalStep.flow = this
    this.generalStep.db = this.db

    this.membersStep = new MembersStep(this.db)
    this.membersStep.flow = this

    this.waiversStep = new WaiversStep()
    this.waiversStep.db = this.db
    this.waiversStep.flow = this

    this.daypickerStep = new DayPickerStep()
    this.daypickerStep.db = this.db
    this.daypickerStep.flow = this

    this.passesStep = new PassesStep(this.db, this)

    this.payStep = new PayStep(this.db, this)

    this.steps = [
      { i: 0, pathname: "/", step: this.generalStep },
      { i: 1, pathname: "/addpeople", step: this.membersStep },
      { i: 2, pathname: "/waivers", step: this.waiversStep },
      { i: 3, pathname: "/chooseday", step: this.daypickerStep },
      { i: 4, pathname: "/passes", step: this.passesStep },
      { i: 5, pathname: "/pay", step: this.payStep }
    ]
  }

  initWatchingEditingMember() {
    autorun(async () => {
      if (this.editingMember) {
        if (this.editingMember.existingObjectIdInLookup) {
          this.editingMember.object = this.db.file.objects[
            this.editingMember.existingObjectIdInLookup
          ] as $Member
        } else {
          const sync = this.db.createSyncRequest()
          if (this.editingMember.uniqueIndex)
            sync.lookupRequests.push({
              lookupKey: this.editingMember.uniqueIndex,
              type: "member"
            })
          if (
            this.editingMember.isValidEmail &&
            this.editingMember.object.email
          )
            sync.lookupRequests.push({
              lookupKey: this.editingMember.object.email,
              type: "member"
            })

          if (
            this.editingMember.isValidPhone &&
            this.editingMember.object.phone
          )
            sync.lookupRequests.push({
              lookupKey: this.editingMember.object.phone,
              type: "member"
            })

          if (sync.lookupRequests.length > 0) this.db.postSyncRequest(sync)
        }
      }
    })
  }

  initBrowserHistory() {
    const history: History = createBrowserHistory()
    autorun(() => {
      if (location.pathname != this.pathname) {
        history.push(this.pathname)
      }
    })
    history.listen((location, action) => {
      this.pathname = location.pathname
    })
  }

  async initFakeBooking() {
    const owner = await this.lookupMember("sotora@gmail.com")

    if (owner) {
      this.currentBooking.setMemberOwner(owner)
      this.currentBooking.addMember(owner)
      this.pathname = "/passes"
    }
  }

  async lookupMember(lookupKey: string): Promise<Member | undefined> {
    const sync = this.db.createSyncRequest()
    sync.lookupRequests.push({ lookupKey, type: "member" })
    const res = await this.db.postSyncRequest(sync)
    if (
      res &&
      res.lookupResponses &&
      res.lookupResponses.length &&
      res.lookupResponses[0].answer
    ) {
      return new Member(this.db, res.file.objects[
        res.lookupResponses[0].answer
      ] as $Member)
    }
    return undefined
  }

  @observable
  currentBooking: Booking

  db = new BookingDB(true)

  async initSessionTypes() {
    const sync = this.db.createSyncRequest()
    sync.lookupRequests = sync.lookupRequests.concat(this.sessionTypeLookups)
    await this.db.postSyncRequest(sync)
  }

  @computed
  get sessionTypeLookups(): LookupRequest[] {
    return this.$sessionTypes.map($sessionType => {
      return {
        lookupKey: $sessionType.name,
        type: "sessionType",
        createIfNotFound: $sessionType
      }
    })
  }

  @computed
  get sessionTypes(): SessionType[] | null {
    const ans: SessionType[] = []
    this.$sessionTypes.forEach((object: $SessionType) => {
      const lookupLocal = this.db.lookupLocal({
        lookupKey: object.name,
        type: "sessionType"
      })
      if (lookupLocal)
        ans.push(new SessionType(this.db, lookupLocal as $SessionType))
    })
    return ans.length ? ans : null
  }

  $sessionTypes: $SessionType[] = [
    {
      $id: uuid(),
      $type: "sessionType",
      $version: uuid(),
      $versionDateString: new Date().toISOString(),
      $parentVersion: null,
      name: "Climb Mo",
      firstPassCatalogItemId: "X2UHXTQLQFN22LAXOBA6KFUT",
      firstPassPriceInCents: 1500,
      secondPassCatalogItemId: "PX7LBX7N6GT6O4OA2TQZGGJT",
      secondPassPriceInCents: 1000,
      capacity: 30,
      durationInMinutes: 60
    },
    {
      $id: uuid(),
      $type: "sessionType",
      $version: uuid(),
      $versionDateString: new Date().toISOString(),
      $parentVersion: null,
      name: "Jump Mo",
      firstPassCatalogItemId: "RVQ66PUGKWKZHMRYENODHPWK",
      firstPassPriceInCents: 1500,
      secondPassCatalogItemId: "7SFN6WFMXRMHJTHJAPLMOWJF",
      secondPassPriceInCents: 1000,
      capacity: 90,
      durationInMinutes: 60
    },
    {
      $id: uuid(),
      $type: "sessionType",
      $version: uuid(),
      $versionDateString: new Date().toISOString(),
      $parentVersion: null,
      name: "Play Mo",
      firstPassCatalogItemId: "RSSSD4NOUSIM2CYT3U6PRSRS",
      firstPassPriceInCents: 1500,
      secondPassCatalogItemId: "BTTEJHOZPPENTV4CXZGXRUTE",
      secondPassPriceInCents: 1000,
      capacity: 180,
      durationInMinutes: 240
    },
    {
      $id: uuid(),
      $type: "sessionType",
      $version: uuid(),
      $versionDateString: new Date().toISOString(),
      $parentVersion: null,
      name: "Observe Mo",
      firstPassCatalogItemId: "None",
      firstPassPriceInCents: 0,
      secondPassCatalogItemId: "none",
      secondPassPriceInCents: 0,
      capacity: 500,
      durationInMinutes: 240
    }
  ]

  async watchTodaysSessions() {
    const neededLookups: LookupRequest[] = []
    const sync = this.db.createSyncRequest()
    if (!this.sessionTypes)
      throw new Error("Must load sessionTypes before calling this method")

    this.sessionTypes.forEach(sessionType => {
      sessionType
        .lookupsForDay(
          this.currentBooking.arrivingDate
            ? this.currentBooking.arrivingDate
            : dayjs()
        )
        .forEach(lookup => {
          const found = this.db.lookupLocal(lookup)
          if (!found) neededLookups.push(lookup)
        })
    })
    sync.lookupRequests = sync.lookupRequests.concat(neededLookups)
    const res = await this.db.postSyncRequest(sync)
    return res
  }

  unWatchTodaysSessions() {}

  @observable
  modalErrorMessage: string | null = null

  @observable
  _modal?: () => Modal

  @computed
  get rootView() {
    if (this.pathname.indexOf("dailyquickview") != -1) {
      return React.createElement(DailyQuickViewComp, {
        model: new DailyQuickView(this.db, this)
      })
    } else {
      return React.createElement(StepsRoot, { flow: this })
    }
  }

  @computed
  get stepComponent() {
    if (this.shouldShowModal) {
      return React.createElement(this.componentMap.modal, {
        model: this.modal
      })
    }
    if (this.notReadyMessage) {
      return React.createElement(this.componentMap.notReady, {
        model: { message: this.notReadyMessage }
      })
    }
    return React.createElement(
      this.componentMap[this.currentStep.name.toLowerCase()],
      {
        model: this.currentStep
      }
    )
  }

  @computed
  get modal(): () => Modal {
    if (this.editingMember) return () => this.editMemberModalParams
    if (!this._modal) throw new Error("No modal set")
    return this._modal
  }

  @observable
  shouldShowModal = false

  @observable.ref
  componentMap: BookingFlowComponents

  @action.bound
  showModal(modal: () => Modal) {
    this._modal = modal
    this.shouldShowModal = true
  }

  @computed
  get userIsLoggedIn() {
    return this.currentBooking.memberOwner !== null
  }

  @computed
  get editMemberModalParams() {
    const m = new Modal(
      this,
      () => {
        this.localEditingMemberOnDone && this.localEditingMemberOnDone()
        this.shouldShowModal = false
        if (this.editingMember)
          this.currentBooking.addMember(this.editingMember)
        this.editingMember = null
      },
      () => {
        this.shouldShowModal = false
        this.editingMember = null
      }
    )

    m.title =
      this.pathname === "/" ? "Add Adult/Parent/Guardian" : "Add Group Member"
    m.primaryButtonText = "Done"
    m.errorMessage = this.editingMember
      ? this.editingMember.invalidMessage
      : undefined

    if (this.editingMember) {
      m.children = React.createElement(this.componentMap.memberForm, {
        model: new MemberForm(this.editingMember, this.db)
      })
      m.primaryDisabled = this.editingMember.invalidMessage != undefined
    }
    return m
  }

  @observable
  editingMember: Member | null = null

  @observable
  localEditingMemberOnDone?: () => void

  @action.bound
  editMember(member: Member, onDone?: () => void) {
    this.editingMember = member
    this.shouldShowModal = true
    this.localEditingMemberOnDone = onDone
  }

  @observable
  generalStep: GeneralStep

  @observable
  membersStep: MembersStep

  @observable
  waiversStep: WaiversStep

  @observable
  daypickerStep: DayPickerStep

  @observable
  passesStep: PassesStep

  @observable
  payStep: PayStep

  @observable
  steps: { i: number; pathname: string; step: Step }[] = []

  @computed
  get pathNameIndex(): number {
    return (
      this.steps &&
      this.steps.filter(step => this.pathname == step.pathname)[0].i
    )
  }

  @computed
  get atGeneralStep() {
    return this.pathNameIndex === 0
  }

  @computed
  get atMembersStep() {
    return this.pathNameIndex === 1
  }

  @computed
  get atWaiverStep() {
    return this.pathNameIndex == 2
  }

  @computed
  get atDayPickerStep() {
    return this.pathNameIndex == 3
  }

  @computed
  get atPassesStep() {
    return this.pathNameIndex == 4
  }

  @computed
  get atPayStep() {
    return this.pathNameIndex == 5
  }

  @computed
  get notReadyMessage() {
    // return "Online sales are temporarily closed.";
    if (this.pathNameIndex > 1 && this.currentBooking.members.length === 0)
      return "Please select some members on your booking before completing this step."

    if (
      this.atWaiverStep &&
      this.currentBooking.waiversNeedingSigned.length <= 0
    )
      return "No members need signed waivers at this time. Please proceed to the next step."

    if (
      this.atDayPickerStep &&
      this.currentBooking.waiversNeedingSigned.length > 0
    )
      return "Please sign waivers for all members of your party before booking a session."

    if (
      this.atPassesStep &&
      this.currentBooking.waiversNeedingSigned.length > 0
    )
      return "Please sign waivers for all members of your party before booking a session."

    if (this.atPayStep && this.currentBooking) {
      if (
        Object.keys(this.currentBooking.currentOrder.sessionGroups).length === 0
      ) {
        return "Please pick one more sessions before paying"
      }

      if (!this.currentBooking.memberOwner) {
        return "Please return to the first General tab and sign in before paying"
      }

      if (this.currentBooking.currentOrder.isComplete) {
        return `🚀 You are ready to go.  Go have some fun at Lava Island!`
      }
    }

    return null
  }

  @computed
  get shouldShowInstruction() {
    if (this.atGeneralStep) return false
    if (this.notReadyMessage) return false
    return true
  }

  @computed
  get shouldShowNavigationButton() {
    if (this.atGeneralStep) return false
    return true
  }

  @computed
  get shouldShowNextButton() {
    return this.steps.length - 1 > this.pathNameIndex && this.pathNameIndex != 0
  }

  @computed
  get shouldShowBackButton() {
    if (this.atGeneralStep) return false
    return true
  }

  @action.bound
  advanceOnEnter(e: any) {
    e.keyCode === 13 ? this.advance() : null
  }

  @action.bound
  advance() {
    if (this.pathNameIndex < this.steps.length - 1) {
      if (this.atPassesStep) {
        try {
          this.db.save(this.currentBooking)
        } catch (err) {
          console.error(err)
        }
      }
      if (
        this.atMembersStep &&
        this.currentBooking.waiversNeedingSigned.length <= 0
      ) {
        this.pathname = "/chooseday"
      } else {
        this.pathname = this.nextPathname
      }
    }
  }

  @action.bound
  goBack() {
    if (this.pathNameIndex != 0) {
      if (this.atMembersStep) {
        if (confirm("Are you sure you want to reset this booking?"))
          this.currentBooking.object.$memberOwner = undefined
        else return
      }
      if (this.shouldSkipWaiverStep) {
        this.pathname = "/addpeople"
      } else {
        this.pathname = this.lastPathName
      }
    }
  }

  @computed
  get lastPathName(): string {
    return this.steps.filter((step, i) => i === this.pathNameIndex - 1)[0]
      .pathname
  }

  @computed
  get shouldSkipWaiverStep() {
    return (
      this.atDayPickerStep &&
      this.currentBooking.waiversNeedingSigned.length <= 0
    )
  }

  @computed
  get shouldShowHelpText() {
    return this.shouldShowInstruction
  }

  @observable
  pathname: string = location.pathname

  @computed
  get nextPathname(): string {
    return this.steps.filter((step, i) => i === this.pathNameIndex + 1)[0]
      .pathname
  }

  @computed
  get currentStep(): Step {
    return this.steps.filter(step => this.pathname == step.pathname)[0].step
  }
}
